import { useEffect, useMemo, useRef, useState } from 'react';

import { Form, type FormInstance } from '@arco-design/web-react';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';

import type { LinkageContext, LinkageRule } from './types';

function addWatchFields(fields: Set<string>, watch: string | readonly string[]): void {
  if (typeof watch === 'string') {
    fields.add(watch);
    return;
  }
  watch.forEach((field) => fields.add(field));
}

function normalizeWatchFields(rules: LinkageRule[]): string[] {
  const fields = new Set<string>();
  rules.forEach((rule) => addWatchFields(fields, rule.watch));
  return Array.from(fields);
}

function ruleIsTriggered(rule: LinkageRule): boolean {
  return Boolean(rule.effect || (rule.derive && rule.target));
}

function readFormValues(form: FormInstance): Record<string, unknown> {
  return form.getFieldsValue() as Record<string, unknown>;
}

function readWatchedFieldSnapshot(form: FormInstance, fields: string[]): Record<string, unknown> {
  const values = readFormValues(form);
  return Object.fromEntries(fields.map((field) => [field, get(values, field)]));
}

function resolveChangedWatchFields(
  fields: string[],
  prev: Record<string, unknown> | undefined,
  next: Record<string, unknown>,
): Set<string> {
  if (!prev) return new Set(fields);
  const changed = new Set<string>();
  for (const field of fields) {
    if (!isEqual(prev[field], next[field])) {
      changed.add(field);
    }
  }
  return changed;
}

function isRuleWatchChanged(rule: LinkageRule, changedFields: Set<string>): boolean {
  const watch = rule.watch;
  if (typeof watch === 'string') return changedFields.has(watch);
  return watch.some((field) => changedFields.has(field));
}

function runLinkageEffects(form: FormInstance, rules: LinkageRule[], changedFields: Set<string>): void {
  const ctx: LinkageContext = { form, values: readFormValues(form) };
  rules.forEach((rule) => {
    if (!rule.effect || !isRuleWatchChanged(rule, changedFields)) return;
    rule.effect(ctx);
  });
}

function runLinkageDerives<TPropsMap extends Record<string, unknown>>(
  form: FormInstance,
  rules: LinkageRule<TPropsMap>[],
): Partial<TPropsMap> {
  const ctx: LinkageContext = { form, values: readFormValues(form) };
  const nextProps: Partial<TPropsMap> = {};

  rules.forEach((rule) => {
    if (!rule.derive || !rule.target) return;
    const derived = rule.derive(ctx);
    if (derived) {
      const key = rule.target;
      (nextProps as Record<string, unknown>)[key] = {
        ...((nextProps[key] as object | undefined) ?? {}),
        ...derived,
      };
    }
  });

  return nextProps;
}

/**
 * 跨 capability 表单联动：唯一允许的外部 Form.useWatch 入口。
 * effect 延迟到 commit 后执行，避免渲染期写 form；derive 在 effect 后按最新表单值计算。
 */
export function useDeployFormLinkage<TPropsMap extends Record<string, unknown>>(
  form: FormInstance,
  rules: LinkageRule<TPropsMap>[],
  enabled = true,
): Partial<TPropsMap> {
  const activeRules = useMemo(() => rules.filter(ruleIsTriggered), [rules]);
  const watchFields = useMemo(() => (enabled ? normalizeWatchFields(activeRules) : []), [activeRules, enabled]);
  const watchedValues = Form.useWatch(watchFields.length ? watchFields : [], form);
  const [effectVersion, setEffectVersion] = useState(0);
  const watchedSnapshotRef = useRef<Record<string, unknown> | undefined>();

  useEffect(() => {
    if (!enabled || !activeRules.length) return;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      const nextSnapshot = readWatchedFieldSnapshot(form, watchFields);
      const changedFields = resolveChangedWatchFields(watchFields, watchedSnapshotRef.current, nextSnapshot);
      watchedSnapshotRef.current = nextSnapshot;
      runLinkageEffects(form, activeRules, changedFields);
      setEffectVersion((v) => v + 1);
    }, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [activeRules, enabled, form, watchedValues]);

  return useMemo(() => {
    if (!enabled || !activeRules.length) return {};
    void effectVersion;
    return runLinkageDerives(form, activeRules);
  }, [activeRules, effectVersion, enabled, form, watchedValues]);
}
